/**
 * Layered scenery round the arena, Territory War style: a far layer and a mid layer behind the
 * ground that scroll slower than the camera, and a foreground layer in front of everything that
 * scrolls faster, so the flat field reads as a place with depth. What is drawn is picked by the
 * level's horizon flavour and whether it has water; where the pieces stand is seeded from the
 * level id, so every client and every rematch sees the same backdrop. The foreground keeps to the
 * bottom edge and the sides, short and translucent, so it never hides a mole.
 */

import { drawPalm, ink, mix, rr, shade } from './cartoon';
import type { LevelDef } from './levels';
import { clamp, hashString, mulberry32 } from './rng';

/** Parallax factors: how much of the camera's travel each layer follows. */
export const FAR_PARALLAX = 0.25;
export const MID_PARALLAX = 0.55;
export const FORE_PARALLAX = 1.25;
/** Foreground pieces are never taller than this many canvas px, and are drawn this translucent. */
const FORE_MAX_H = 88;
const FORE_ALPHA = 0.7;

/** Where the camera is and how big the canvas is, so a layer can place itself in screen px. */
export interface SceneryView {
  camX: number;
  camY: number;
  zoom: number;
  /** Canvas size in device px. */
  w: number;
  h: number;
  /** Screen y of the line the far and mid layers stand on (the waterline, or where the ground would be). */
  baseY: number;
  now: number;
}

type Horizon = 'islands' | 'mesas' | 'city' | 'storm' | 'moon' | 'dunes' | 'cave';

interface Piece {
  /** Position along the layer, in layer px; the layer repeats every `period`. */
  x: number;
  /** Which prop of the layer's set. */
  kind: number;
  /** Size multiplier about 1. */
  s: number;
  /** A per-piece phase for its animation so nothing moves in lockstep. */
  ph: number;
  /** A per-piece random for whatever the prop wants (window pattern, lean, colour pick). */
  r: number;
}

/**
 * Reads the horizon as a string: 'cave' is not in LevelStyle yet, and anything unknown on a dry
 * level is treated as underground rather than left bare.
 */
function horizonOf(level: LevelDef): Horizon {
  const h = level.style.horizon as string;
  if (h === 'islands' || h === 'mesas' || h === 'city' || h === 'storm' || h === 'moon' || h === 'dunes') return h;
  return 'cave';
}

export class Scenery {
  private readonly horizon: Horizon;
  private readonly far: Piece[] = [];
  private readonly mid: Piece[] = [];
  private readonly fore: Piece[] = [];
  /** The layers repeat every this many px so they cover the view however far out the camera pulls. */
  private readonly period: number;
  private readonly farColor: string;
  private readonly midColor: string;
  private readonly foreColor: string;
  private readonly wet: boolean;

  constructor(private readonly level: LevelDef) {
    this.horizon = horizonOf(level);
    this.wet = level.waterY !== null;
    this.period = level.width + 1600;
    const style = level.style;
    const cave = this.horizon === 'cave';
    // Each layer a step darker than the one behind it: the ridge the renderer paints, then far, mid, and a near-black foreground.
    this.farColor = cave ? '#2b2433' : shade(style.far, -16);
    this.midColor = cave ? '#1c1722' : shade(style.near, -22);
    this.foreColor = cave ? '#0e0b12' : mix(shade(style.near, -70), '#000000', 0.35);
    const rng = mulberry32(hashString(level.id) ^ 0x5ce9);
    const fill = (out: Piece[], count: number, kinds: number) => {
      const gap = this.period / count;
      for (let i = 0; i < count; i++) {
        out.push({ x: i * gap + rng() * gap * 0.7, kind: Math.floor(rng() * kinds), s: 0.75 + rng() * 0.5, ph: rng() * Math.PI * 2, r: rng() });
      }
    };
    const per = (n: number) => Math.max(3, Math.round((this.period / 1000) * n));
    switch (this.horizon) {
      case 'islands':
        fill(this.far, per(2.2), 1);
        fill(this.mid, per(4), 2);
        fill(this.fore, per(3), 2);
        break;
      case 'mesas':
        fill(this.far, per(2.4), 1);
        fill(this.mid, per(4), 2);
        fill(this.fore, per(4), 1);
        break;
      case 'city':
        fill(this.far, per(9), 1);
        fill(this.mid, per(4), 2);
        fill(this.fore, per(2.5), 1);
        break;
      case 'storm':
        fill(this.far, per(2.6), 1);
        fill(this.mid, per(3), 1);
        fill(this.fore, per(5), 1);
        break;
      case 'moon':
        fill(this.far, per(5), 1);
        fill(this.mid, per(3.5), 1);
        fill(this.fore, per(8), 1);
        break;
      case 'dunes':
        fill(this.far, per(2), 1);
        fill(this.mid, per(3), 1);
        fill(this.fore, per(4), 1);
        break;
      case 'cave':
        fill(this.far, per(4), 1);
        fill(this.mid, per(5), 2);
        fill(this.fore, per(2.5), 1);
        break;
    }
  }

  /**
   * Walks a layer's pieces over every repeat that touches the screen, calling back with each one's
   * screen x. The layer is anchored so that at the map's centre it lines up with the world, and
   * repeats are added until the view is covered: pulled out between turns the view can be wider
   * than one repeat.
   */
  private each(pieces: Piece[], f: number, v: SceneryView, cb: (p: Piece, sx: number) => void): void {
    // A layer with factor f moves f times as far on screen as the camera does (far 0.25, near 1.25): its world position
    // follows the camera by 1 - f of the way, and equals the world's own at the map's centre.
    const shift = (v.camX - this.level.width / 2) * (1 - f);
    const span = v.w / v.zoom;
    const P = this.period;
    const k0 = Math.floor((v.camX - span / 2 - 600) / P) - 1;
    const k1 = Math.ceil((v.camX + span / 2 + 600) / P) + 1;
    for (const p of pieces) {
      for (let k = k0; k <= k1; k++) {
        const wx = p.x + k * P - 800 + shift;
        const sx = (wx - v.camX) * v.zoom + v.w / 2;
        if (sx < -400 * v.zoom || sx > v.w + 400 * v.zoom) continue;
        cb(p, sx);
      }
    }
  }

  /** The scale props draw at: they shrink with the camera, but never to nothing. */
  private propScale(v: SceneryView, lo: number, hi: number): number {
    return clamp(v.zoom, lo, hi);
  }

  // ---------- far ----------

  drawFar(ctx: CanvasRenderingContext2D, v: SceneryView): void {
    const z = this.propScale(v, 0.45, 1.5);
    const color = this.farColor;
    const lit = mix(color, '#ffffff', 0.18);
    ctx.save();
    ctx.lineJoin = 'round';
    switch (this.horizon) {
      case 'islands':
        this.each(this.far, FAR_PARALLAX, v, (p, sx) => {
          const w = 220 * p.s * z;
          const h = 54 * p.s * z;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(sx - w / 2, v.baseY + 2);
          ctx.quadraticCurveTo(sx - w * 0.2, v.baseY - h, sx + w * 0.1, v.baseY - h * 0.9);
          ctx.quadraticCurveTo(sx + w * 0.4, v.baseY - h * 0.6, sx + w / 2, v.baseY + 2);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = lit;
          ctx.beginPath();
          ctx.moveTo(sx - w * 0.3, v.baseY - h * 0.55);
          ctx.quadraticCurveTo(sx - w * 0.1, v.baseY - h * 0.85, sx + w * 0.1, v.baseY - h * 0.78);
          ctx.quadraticCurveTo(sx - w * 0.1, v.baseY - h * 0.62, sx - w * 0.3, v.baseY - h * 0.55);
          ctx.fill();
          drawPalm(ctx, sx + w * 0.05, v.baseY - h * 0.85, 30 * p.s * z, shade(color, -30), shade(color, -22), p.r > 0.5 ? 1 : -1);
        });
        break;
      case 'mesas':
        this.each(this.far, FAR_PARALLAX, v, (p, sx) => {
          const w = 260 * p.s * z;
          const h = (90 + p.r * 50) * p.s * z;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(sx - w / 2, v.baseY + 2);
          ctx.lineTo(sx - w * 0.36, v.baseY - h);
          ctx.lineTo(sx + w * 0.3, v.baseY - h);
          ctx.lineTo(sx + w / 2, v.baseY + 2);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = lit;
          ctx.fillRect(sx - w * 0.36, v.baseY - h, w * 0.66, 5 * z);
          ctx.fillStyle = shade(color, -14);
          ctx.fillRect(sx - w * 0.42, v.baseY - h * 0.55, w * 0.84, 4 * z);
        });
        break;
      case 'city':
        this.each(this.far, FAR_PARALLAX, v, (p, sx) => {
          const w = (54 + p.r * 50) * z;
          const h = (90 + p.s * 110) * z;
          ctx.fillStyle = color;
          ctx.fillRect(sx - w / 2, v.baseY - h, w, h + 4);
          if (p.kind === 0 && p.r > 0.6) ctx.fillRect(sx - 2 * z, v.baseY - h - 18 * z, 4 * z, 18 * z);
          // Windows: a grid, some lit, the lit ones blinking off and on slowly.
          const cols = Math.max(2, Math.floor(w / (12 * z)));
          const rows = Math.max(3, Math.floor(h / (14 * z)));
          const wr = mulberry32(Math.floor(p.r * 1e6));
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const on = wr() < 0.45;
              const blink = Math.sin(v.now / 1400 + p.ph + r * 1.7 + c * 2.3) > -0.85;
              ctx.fillStyle = on && blink ? '#ffe08a' : shade(color, -18);
              ctx.fillRect(sx - w / 2 + (c + 0.35) * (w / cols), v.baseY - h + (r + 0.35) * (h / rows), (w / cols) * 0.4, (h / rows) * 0.45);
            }
          }
        });
        break;
      case 'storm':
        this.each(this.far, FAR_PARALLAX, v, (p, sx) => {
          const w = 200 * p.s * z;
          const h = (120 + p.r * 70) * p.s * z;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(sx - w / 2, v.baseY + 2);
          ctx.lineTo(sx - w * 0.4, v.baseY - h * 0.6);
          ctx.lineTo(sx - w * 0.2, v.baseY - h * 0.75);
          ctx.lineTo(sx - w * 0.05, v.baseY - h);
          ctx.lineTo(sx + w * 0.2, v.baseY - h * 0.8);
          ctx.lineTo(sx + w * 0.35, v.baseY - h * 0.85);
          ctx.lineTo(sx + w / 2, v.baseY + 2);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = lit;
          ctx.beginPath();
          ctx.moveTo(sx - w * 0.2, v.baseY - h * 0.75);
          ctx.lineTo(sx - w * 0.05, v.baseY - h);
          ctx.lineTo(sx + w * 0.02, v.baseY - h * 0.7);
          ctx.closePath();
          ctx.fill();
        });
        // Rain: streaks falling across the whole sky, fixed to the screen.
        ctx.strokeStyle = 'rgba(220,230,240,0.22)';
        ctx.lineWidth = 1.5 * z;
        ctx.lineCap = 'round';
        const fall = (v.now / 2.2) % 400;
        for (let i = 0; i < 70; i++) {
          const rx = (((i * 7919) % 1000) / 1000) * (v.w + 200) - 100 + (fall * 0.3);
          const ry = ((((i * 104729) % 1000) / 1000) * (v.h + 400) + fall) % (v.h + 400) - 200;
          ctx.beginPath();
          ctx.moveTo(rx, ry);
          ctx.lineTo(rx - 4 * z, ry + 22 * z);
          ctx.stroke();
        }
        break;
      case 'moon':
        this.each(this.far, FAR_PARALLAX, v, (p, sx) => {
          const w = (60 + p.r * 80) * p.s * z;
          const h = w * 0.22;
          ctx.fillStyle = shade(color, -12);
          ctx.beginPath();
          ctx.ellipse(sx, v.baseY - h * 0.2, w / 2, h / 2, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = lit;
          ctx.lineWidth = 3 * z;
          ctx.beginPath();
          ctx.ellipse(sx, v.baseY - h * 0.35, w / 2, h / 2, 0, Math.PI, Math.PI * 2);
          ctx.stroke();
        });
        break;
      case 'dunes':
        this.each(this.far, FAR_PARALLAX, v, (p, sx) => {
          const w = 420 * p.s * z;
          const h = 60 * p.s * z;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(sx - w / 2, v.baseY + 2);
          ctx.quadraticCurveTo(sx - w * 0.1, v.baseY - h * 1.1, sx + w * 0.15, v.baseY - h * 0.95);
          ctx.quadraticCurveTo(sx + w * 0.35, v.baseY - h * 0.8, sx + w / 2, v.baseY + 2);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = lit;
          ctx.lineWidth = 3 * z;
          ctx.beginPath();
          ctx.moveTo(sx - w * 0.3, v.baseY - h * 0.45);
          ctx.quadraticCurveTo(sx - w * 0.05, v.baseY - h * 1.02, sx + w * 0.15, v.baseY - h * 0.95);
          ctx.stroke();
        });
        break;
      case 'cave':
        // The rock face behind everything, with clusters of crystals that pulse.
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, v.w, v.h);
        this.each(this.far, FAR_PARALLAX, v, (p, sx) => {
          const base = v.baseY - (p.r - 0.5) * 300 * z;
          const n = 2 + Math.floor(p.s * 3);
          const hue = p.kind === 0 && p.r > 0.5 ? '#7ee8ff' : '#d58cff';
          const glow = 0.5 + 0.5 * Math.sin(v.now / 900 + p.ph);
          ctx.fillStyle = `rgba(${hue === '#7ee8ff' ? '126,232,255' : '213,140,255'},${0.08 + glow * 0.1})`;
          ctx.beginPath();
          ctx.arc(sx, base - 10 * z, 60 * p.s * z, 0, Math.PI * 2);
          ctx.fill();
          for (let i = 0; i < n; i++) {
            const a = -Math.PI / 2 + (i - (n - 1) / 2) * 0.55;
            const len = (26 + ((i * 37) % 20)) * p.s * z;
            ctx.save();
            ctx.translate(sx + (i - (n - 1) / 2) * 9 * z, base);
            ctx.rotate(a + Math.PI / 2);
            ctx.beginPath();
            ctx.moveTo(-6 * z, 0);
            ctx.lineTo(0, -len);
            ctx.lineTo(6 * z, 0);
            ctx.closePath();
            ctx.globalAlpha = 0.55 + glow * 0.45;
            ink(ctx, hue, 2 * z, shade(color, -20));
            ctx.globalAlpha = 1;
            ctx.restore();
          }
        });
        break;
    }
    ctx.restore();
  }

  // ---------- mid ----------

  drawMid(ctx: CanvasRenderingContext2D, v: SceneryView): void {
    const z = this.propScale(v, 0.5, 1.6);
    const color = this.midColor;
    ctx.save();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    switch (this.horizon) {
      case 'islands':
        this.each(this.mid, MID_PARALLAX, v, (p, sx) => {
          if (p.kind === 0) {
            const sway = Math.sin(v.now / 1500 + p.ph) * 0.08;
            ctx.save();
            ctx.translate(sx, v.baseY + 6 * z);
            ctx.rotate(sway);
            drawPalm(ctx, 0, 0, 70 * p.s * z, shade(color, -10), color, p.r > 0.5 ? 1 : -1);
            ctx.restore();
          } else {
            this.rock(ctx, sx, v.baseY + 6 * z, 44 * p.s * z, color);
          }
        });
        break;
      case 'mesas':
        this.each(this.mid, MID_PARALLAX, v, (p, sx) => {
          if (p.kind === 0) this.cactus(ctx, sx, v.baseY + 6 * z, 64 * p.s * z, color, p.r);
          else this.rock(ctx, sx, v.baseY + 6 * z, 40 * p.s * z, color);
        });
        break;
      case 'city':
        this.each(this.mid, MID_PARALLAX, v, (p, sx) => {
          if (p.kind === 0) {
            // A lamp post, lit when the sky is dark.
            const h = 86 * z;
            ctx.strokeStyle = color;
            ctx.lineWidth = 4 * z;
            ctx.beginPath();
            ctx.moveTo(sx, v.baseY + 6 * z);
            ctx.lineTo(sx, v.baseY - h);
            ctx.quadraticCurveTo(sx, v.baseY - h - 12 * z, sx + 14 * z, v.baseY - h - 10 * z);
            ctx.stroke();
            const flick = 0.85 + 0.15 * Math.sin(v.now / 90 + p.ph);
            ctx.fillStyle = `rgba(255,224,138,${0.18 * flick})`;
            ctx.beginPath();
            ctx.arc(sx + 14 * z, v.baseY - h - 6 * z, 22 * z, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#ffe08a';
            ctx.beginPath();
            ctx.arc(sx + 14 * z, v.baseY - h - 6 * z, 4 * z, 0, Math.PI * 2);
            ctx.fill();
          } else {
            // A run of fence: posts and two rails.
            const w = 120 * p.s * z;
            ctx.strokeStyle = color;
            ctx.lineWidth = 3 * z;
            for (let i = 0; i <= 4; i++) {
              const px = sx - w / 2 + (i / 4) * w;
              ctx.beginPath();
              ctx.moveTo(px, v.baseY + 6 * z);
              ctx.lineTo(px, v.baseY - 26 * z);
              ctx.stroke();
            }
            for (const ry of [-20, -8]) {
              ctx.beginPath();
              ctx.moveTo(sx - w / 2, v.baseY + ry * z);
              ctx.lineTo(sx + w / 2, v.baseY + ry * z);
              ctx.stroke();
            }
          }
        });
        break;
      case 'storm':
        // Gulls wheeling over the water: they drift along the layer and flap.
        this.each(this.mid, MID_PARALLAX, v, (p, sx) => {
          const drift = ((v.now / 40) * (0.6 + p.r * 0.8)) % this.period;
          const gx = sx + (drift - this.period / 2) * v.zoom * 0.2;
          const gy = v.baseY - (120 + p.r * 160) * z + Math.sin(v.now / 700 + p.ph) * 12 * z;
          const flap = Math.sin(v.now / 110 + p.ph) * 7 * z;
          const s = 10 * p.s * z;
          ctx.strokeStyle = color;
          ctx.lineWidth = 2.5 * z;
          ctx.beginPath();
          ctx.moveTo(gx - s, gy - flap);
          ctx.quadraticCurveTo(gx - s * 0.5, gy, gx, gy);
          ctx.quadraticCurveTo(gx + s * 0.5, gy, gx + s, gy - flap);
          ctx.stroke();
        });
        break;
      case 'moon':
        this.each(this.mid, MID_PARALLAX, v, (p, sx) => {
          const h = (70 + p.r * 70) * p.s * z;
          const w = 26 * p.s * z;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(sx - w, v.baseY + 6 * z);
          ctx.lineTo(sx - w * 0.3, v.baseY - h * 0.7);
          ctx.lineTo(sx + w * 0.1, v.baseY - h);
          ctx.lineTo(sx + w * 0.5, v.baseY - h * 0.6);
          ctx.lineTo(sx + w, v.baseY + 6 * z);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = mix(color, '#ffffff', 0.12);
          ctx.beginPath();
          ctx.moveTo(sx - w * 0.3, v.baseY - h * 0.7);
          ctx.lineTo(sx + w * 0.1, v.baseY - h);
          ctx.lineTo(sx + w * 0.05, v.baseY - h * 0.5);
          ctx.closePath();
          ctx.fill();
        });
        break;
      case 'dunes':
        this.each(this.mid, MID_PARALLAX, v, (p, sx) => this.deadTree(ctx, sx, v.baseY + 6 * z, 70 * p.s * z, color, p.r));
        break;
      case 'cave':
        this.each(this.mid, MID_PARALLAX, v, (p, sx) => {
          if (p.kind === 0) {
            // A stalactite from the top of the screen.
            const h = (90 + p.r * 120) * p.s * z;
            const w = 22 * p.s * z;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(sx - w, -4);
            ctx.quadraticCurveTo(sx - w * 0.3, h * 0.5, sx, h);
            ctx.quadraticCurveTo(sx + w * 0.3, h * 0.5, sx + w, -4);
            ctx.closePath();
            ctx.fill();
          } else {
            // Roots snaking down out of the ceiling.
            ctx.strokeStyle = shade('#4a3328', -8);
            ctx.lineWidth = 3.5 * z;
            const len = (80 + p.r * 100) * z;
            for (const side of [-1, 1]) {
              ctx.beginPath();
              ctx.moveTo(sx, -4);
              ctx.bezierCurveTo(sx + side * 20 * z, len * 0.3, sx - side * 24 * z, len * 0.6, sx + side * 10 * z, len);
              ctx.stroke();
            }
          }
        });
        break;
    }
    ctx.restore();
  }

  // ---------- foreground ----------

  /** The near layer: at the bottom edge and the sides, translucent, over the field but under the aim preview. */
  drawFore(ctx: CanvasRenderingContext2D, v: SceneryView): void {
    const z = this.propScale(v, 0.7, 1.15);
    const color = this.foreColor;
    const floor = v.h + 2;
    ctx.save();
    ctx.globalAlpha = FORE_ALPHA;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    switch (this.horizon) {
      case 'islands':
        this.each(this.fore, FORE_PARALLAX, v, (p, sx) => {
          if (p.kind === 0 || !this.wet) this.reeds(ctx, sx, floor, 70 * z, color, v.now, p.ph, p.r);
          else this.buoy(ctx, sx, floor - 16 * z + Math.sin(v.now / 600 + p.ph) * 5 * z, 22 * z, Math.sin(v.now / 800 + p.ph) * 0.12);
        });
        break;
      case 'mesas':
        this.each(this.fore, FORE_PARALLAX, v, (p, sx) => this.tufts(ctx, sx, floor, 34 * z, color, v.now, p.ph));
        this.tumbleweed(ctx, v, z, color);
        break;
      case 'city':
        this.each(this.fore, FORE_PARALLAX, v, (p, sx) => {
          const w = 220 * p.s * z;
          ctx.strokeStyle = color;
          ctx.lineWidth = 5 * z;
          ctx.beginPath();
          ctx.moveTo(sx - w / 2, floor - 40 * z);
          ctx.lineTo(sx + w / 2, floor - 40 * z);
          ctx.stroke();
          ctx.lineWidth = 4 * z;
          for (let i = 0; i <= 6; i++) {
            const px = sx - w / 2 + (i / 6) * w;
            ctx.beginPath();
            ctx.moveTo(px, floor);
            ctx.lineTo(px, floor - 40 * z);
            ctx.stroke();
          }
        });
        this.pigeon(ctx, v, z, color);
        break;
      case 'storm':
        this.each(this.fore, FORE_PARALLAX, v, (p, sx) => {
          // Spray: a burst of white drops thrown up from the bottom edge, each piece on its own timer.
          const t = ((v.now / 1300) + p.ph) % 1;
          const a = (1 - t) * 0.9;
          ctx.fillStyle = `rgba(235,245,255,${a})`;
          for (let i = 0; i < 6; i++) {
            const dx = (i - 2.5) * 11 * z * (0.4 + t);
            const dy = -(40 + (i % 3) * 14) * z * Math.sin(t * Math.PI);
            ctx.beginPath();
            ctx.arc(sx + dx, floor + dy, (3 + (i % 2) * 2) * z, 0, Math.PI * 2);
            ctx.fill();
          }
        });
        break;
      case 'moon':
        this.each(this.fore, FORE_PARALLAX, v, (p, sx) => {
          // Dust: slow motes drifting sideways along the bottom.
          const dx = Math.sin(v.now / 1800 + p.ph) * 30 * z;
          const dy = -((v.now / 60 + p.r * 400) % 70) * z;
          ctx.fillStyle = 'rgba(220,220,230,0.5)';
          ctx.beginPath();
          ctx.arc(sx + dx, floor + dy, 2.5 * z, 0, Math.PI * 2);
          ctx.fill();
          this.rock(ctx, sx + 30 * z, floor, 16 * p.s * z, color);
        });
        break;
      case 'dunes':
        this.each(this.fore, FORE_PARALLAX, v, (p, sx) => this.tufts(ctx, sx, floor, 44 * z, color, v.now, p.ph));
        break;
      case 'cave':
        // Vines hang from the top at the left and right of the screen only, so the middle stays clear; drips fall off their ends.
        this.each(this.fore, FORE_PARALLAX, v, (p, sx) => {
          const edge = Math.min(sx, v.w - sx);
          if (edge > v.w * 0.22) return;
          const len = Math.min(FORE_MAX_H, (60 + p.r * 30) * z);
          const sway = Math.sin(v.now / 1300 + p.ph) * 6 * z;
          ctx.strokeStyle = '#1f3a1c';
          ctx.lineWidth = 3.5 * z;
          ctx.beginPath();
          ctx.moveTo(sx, -2);
          ctx.quadraticCurveTo(sx + sway * 0.5, len * 0.5, sx + sway, len);
          ctx.stroke();
          ctx.fillStyle = '#2d5a28';
          for (let i = 1; i <= 3; i++) {
            ctx.beginPath();
            ctx.ellipse(sx + sway * (i / 3) + (i % 2 ? 5 : -5) * z, len * (i / 3) - 6 * z, 6 * z, 3 * z, (i % 2 ? 0.6 : -0.6), 0, Math.PI * 2);
            ctx.fill();
          }
          const t = ((v.now / 2400) + p.ph) % 1;
          if (t < 0.5) {
            ctx.fillStyle = 'rgba(170,220,255,0.8)';
            ctx.beginPath();
            ctx.ellipse(sx + sway, len + t * t * 4 * v.h, 2 * z, 3.5 * z, 0, 0, Math.PI * 2);
            ctx.fill();
          }
        });
        break;
    }
    ctx.restore();
  }

  // ---------- props ----------

  private rock(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, color: string): void {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x - w / 2, y);
    ctx.quadraticCurveTo(x - w * 0.45, y - w * 0.5, x - w * 0.1, y - w * 0.55);
    ctx.quadraticCurveTo(x + w * 0.3, y - w * 0.6, x + w / 2, y);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = mix(color, '#ffffff', 0.12);
    ctx.beginPath();
    ctx.moveTo(x - w * 0.3, y - w * 0.3);
    ctx.quadraticCurveTo(x - w * 0.2, y - w * 0.5, x - w * 0.05, y - w * 0.5);
    ctx.lineTo(x - w * 0.1, y - w * 0.3);
    ctx.closePath();
    ctx.fill();
  }

  private cactus(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, color: string, r: number): void {
    const w = h * 0.18;
    ctx.fillStyle = color;
    rr(ctx, x - w / 2, y - h, w, h, w / 2);
    ctx.fill();
    const arms: [number, number][] = r > 0.3 ? [[-1, 0.55], [1, 0.4]] : [[r > 0.15 ? 1 : -1, 0.5]];
    for (const [side, at] of arms) {
      const ay = y - h * at;
      rr(ctx, x + side * (w * 0.4), ay - w * 0.4, side > 0 ? w * 1.4 : -w * 1.4, w * 0.8, w * 0.4);
      ctx.fill();
      rr(ctx, x + side * (w * 1.4) - w * 0.4, ay - h * 0.28, w * 0.8, h * 0.28 + w * 0.4, w * 0.4);
      ctx.fill();
    }
  }

  private deadTree(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, color: string, r: number): void {
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, h * 0.07);
    const lean = (r - 0.5) * 0.6;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.quadraticCurveTo(x + lean * h * 0.3, y - h * 0.5, x + lean * h * 0.5, y - h);
    ctx.stroke();
    ctx.lineWidth = Math.max(1.5, h * 0.04);
    for (const [at, side, len] of [[0.5, -1, 0.4], [0.65, 1, 0.35], [0.85, -1, 0.25]] as [number, number, number][]) {
      const bx = x + lean * h * 0.3 * at * 1.4;
      const by = y - h * at;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.quadraticCurveTo(bx + side * h * len * 0.6, by - h * len * 0.4, bx + side * h * len, by - h * len * 0.9);
      ctx.stroke();
    }
  }

  private reeds(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, color: string, now: number, ph: number, r: number): void {
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    const n = 4 + Math.floor(r * 3);
    for (let i = 0; i < n; i++) {
      const bx = x + (i - (n - 1) / 2) * 7;
      const bh = h * (0.6 + ((i * 7) % 5) / 10);
      const sway = Math.sin(now / 1100 + ph + i * 0.7) * 6;
      ctx.beginPath();
      ctx.moveTo(bx, y);
      ctx.quadraticCurveTo(bx + sway * 0.3, y - bh * 0.6, bx + sway, y - bh);
      ctx.stroke();
      if (i % 2 === 0) {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(bx + sway, y - bh, 3, 8, 0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private tufts(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, color: string, now: number, ph: number): void {
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    for (let i = 0; i < 7; i++) {
      const a = -Math.PI / 2 + (i - 3) * 0.22 + Math.sin(now / 900 + ph + i) * 0.06;
      const len = h * (0.6 + ((i * 5) % 4) / 10);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + Math.cos(a) * len * 0.5, y + Math.sin(a) * len * 0.7, x + Math.cos(a) * len * 1.2, y + Math.sin(a) * len);
      ctx.stroke();
    }
  }

  private buoy(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, tilt: number): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(tilt);
    ctx.beginPath();
    ctx.moveTo(-s * 0.6, 0);
    ctx.lineTo(-s * 0.3, -s);
    ctx.lineTo(s * 0.3, -s);
    ctx.lineTo(s * 0.6, 0);
    ctx.closePath();
    ink(ctx, '#d6362c', 2.5);
    ctx.fillStyle = '#f4f1e6';
    ctx.fillRect(-s * 0.42, -s * 0.65, s * 0.84, s * 0.22);
    rr(ctx, -s * 0.12, -s * 1.3, s * 0.24, s * 0.3, 2);
    ink(ctx, '#ffcc4d', 2);
    ctx.restore();
  }

  /** One tumbleweed rolls across the bottom of the screen every so often. */
  private tumbleweed(ctx: CanvasRenderingContext2D, v: SceneryView, z: number, color: string): void {
    const cycle = 16000;
    const t = (v.now % cycle) / cycle;
    if (t > 0.45) return;
    const k = t / 0.45;
    const x = -40 * z + k * (v.w + 80 * z);
    const y = v.h - 16 * z - Math.abs(Math.sin(k * Math.PI * 7)) * 14 * z;
    const r = 15 * z;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(k * 40);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5 * z;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.stroke();
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      ctx.quadraticCurveTo(Math.cos(a + 1.5) * r * 0.4, Math.sin(a + 1.5) * r * 0.4, Math.cos(a + 3.5) * r, Math.sin(a + 3.5) * r);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** A pigeon flaps across low over the railings now and then. */
  private pigeon(ctx: CanvasRenderingContext2D, v: SceneryView, z: number, color: string): void {
    const cycle = 13000;
    const t = (v.now % cycle) / cycle;
    if (t > 0.3) return;
    const k = t / 0.3;
    const x = v.w + 30 * z - k * (v.w + 60 * z);
    const y = v.h - 60 * z + Math.sin(k * 9) * 8 * z;
    const flap = Math.sin(v.now / 70) * 6 * z;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(x, y, 8 * z, 4.5 * z, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x - 8 * z, y - 2 * z, 3 * z, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5 * z;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 4 * z, y - 9 * z - flap);
    ctx.moveTo(x, y);
    ctx.lineTo(x - 2 * z, y - 9 * z + flap);
    ctx.stroke();
  }
}
